import { useState } from 'react'
import axios from 'axios'
import { authHeaders } from '../Comisiones'
import PlanFormModal from './PlanFormModal'
import styles from './Components.module.css'

const API = import.meta.env.VITE_API_GATEWAY_URL

const TIPO_LABELS = {
    base: 'Comisión Base',
    escalonada_venta: 'Escalonada Venta',
    escalonada_recaudo: 'Escalonada Recaudo',
    nuevo_cliente: 'Nuevos Clientes',
    bono: 'Bonificación',
}

function PlanesManager({ empresaId, periodo, planes, onRefresh, setMsg, readOnly = false }) {
    const [modalPlan, setModalPlan] = useState(null)   // null | 'new' | planObject
    const [deleting, setDeleting]   = useState(null)

    const handleDelete = async (plan) => {
        if (!window.confirm(`¿Eliminar plan de ${plan.gestor_nombre || plan.gestor_email} para ${plan.periodo}?`)) return
        setDeleting(plan.id)
        try {
            const headers = await authHeaders()
            const enc = encodeURIComponent(plan.gestor_email)
            await axios.delete(`${API}/comisiones/${empresaId}/planes/${plan.periodo}/${enc}`, { headers })
            setMsg({ type: 'success', text: 'Plan eliminado correctamente' })
            onRefresh()
        } catch (err) {
            setMsg({ type: 'error', text: err.response?.data?.error || err.message })
        } finally {
            setDeleting(null) }
    }

    return (
        <div className={styles.section}>
            <div className={styles.sectionHeader}>
                <div>
                    <h2 className={styles.sectionTitle}>📋 Planes de Comisión — {periodo}</h2>
                    <p className={styles.sectionSub}>
                        {readOnly
                            ? 'Visualiza las reglas de comisión asignadas a tu contrato'
                            : 'Configura las reglas de comisión por gestor y período'}
                    </p>
                </div>
                {!readOnly && (
                    <button
                        id="btn-nuevo-plan"
                        className={styles.btnPrimary}
                        onClick={() => setModalPlan('new')}
                    >
                        ➕ Nuevo plan
                    </button>
                )}
            </div>

            {planes.length === 0 ? (
                <div className={styles.empty}>
                    <span>📋</span>
                    <p>No hay planes configurados para {periodo}</p>
                    {!readOnly && <small>Crea un nuevo plan para comenzar</small>}
                </div>
            ) : (
                <div className={styles.planesGrid}>
                    {planes.map(plan => (
                        <div key={plan.id} className={styles.planCard}>
                            {/* Card header */}
                            <div className={styles.planCardHeader}>
                                <div className={styles.planAvatar}>
                                    {(plan.gestor_nombre || plan.gestor_email).charAt(0).toUpperCase()}
                                </div>
                                <div className={styles.planCardInfo}>
                                    <h3>{plan.gestor_nombre || plan.gestor_email}</h3>
                                    <p>{plan.gestor_email}</p>
                                    {plan.region && <span className={styles.regionBadge}>📍 {plan.region}</span>}
                                    {plan.cargo  && <span className={styles.cargoBadge}>{plan.cargo}</span>}
                                </div>
                                <span className={styles.periodoBadge}>{plan.periodo}</span>
                            </div>

                            {/* Reglas resumidas */}
                            <div className={styles.reglasList}>
                                {plan.reglas?.slice(0, 4).map(r => (
                                    <div key={r.id} className={styles.reglaChip}>
                                        <span className={styles.reglaTipo}>{TIPO_LABELS[r.tipo] || r.tipo}</span>
                                        <span className={styles.reglaNombre}>{r.nombre}</span>
                                    </div>
                                ))}
                                {plan.reglas?.length > 4 && (
                                    <span className={styles.masReglas}>+{plan.reglas.length - 4} más</span>
                                )}
                            </div>

                            {/* Metas */}
                            {plan.metas && (
                                <div className={styles.metasRow}>
                                    <div className={styles.metaItem}>
                                        <span>Meta período</span>
                                        <strong>
                                            ${Number(plan.metas.periodo || plan.metas.q1 || 0).toLocaleString('es-CO')}
                                        </strong>
                                    </div>
                                    <div className={styles.metaItem}>
                                        <span>Meta anual</span>
                                        <strong>${Number(plan.metas.anual || 0).toLocaleString('es-CO')}</strong>
                                    </div>
                                </div>
                            )}

                            {/* Actions */}
                            {!readOnly && (
                                <div className={styles.planActions}>
                                    <button
                                        id={`btn-editar-plan-${plan.gestor_email}`}
                                        className={styles.btnSecondary}
                                        onClick={() => setModalPlan(plan)}
                                    >✏️ Editar</button>
                                    <button
                                        className={styles.btnDanger}
                                        onClick={() => handleDelete(plan)}
                                        disabled={deleting === plan.id}
                                    >
                                        {deleting === plan.id ? '...' : '🗑️ Eliminar'}
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Modal crear/editar plan */}
            {modalPlan !== null && (
                <PlanFormModal
                    empresaId={empresaId}
                    periodo={periodo}
                    plan={modalPlan === 'new' ? null : modalPlan}
                    onClose={() => setModalPlan(null)}
                    onSave={() => { setModalPlan(null); onRefresh(); setMsg({ type: 'success', text: '✅ Plan guardado correctamente' }) }}
                    setMsg={setMsg}
                />
            )}
        </div>
    )
}

export default PlanesManager
